import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "w-full bg-paper border border-ink/15 focus:border-alta outline-none px-4 py-3 text-sm font-light text-ink transition-colors";

function emptyItem(fields) {
  const obj = {};
  fields.forEach((f) => {
    obj[f.name] = f.type === "list" ? [] : f.type === "checkbox" ? true : f.type === "number" ? 0 : "";
  });
  return obj;
}

export default function ResourcePage({ title, subtitle, endpoint, fields, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {form, id|null}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`${endpoint}?all=true`)
      .then((r) => setItems(r.data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))))
      .catch(() => toast.error("Could not load items"))
      .finally(() => setLoading(false));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setEditing({ id: null, form: emptyItem(fields) });
  const openEdit = (item) => {
    const form = {};
    fields.forEach((f) => { form[f.name] = item[f.name] ?? emptyItem(fields)[f.name]; });
    setEditing({ id: item.id, form });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin${endpoint}/${editing.id}`, editing.form);
      } else {
        await api.post(`/admin${endpoint}`, editing.form);
      }
      toast.success(editing.id ? "Updated" : "Created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (item) => {
    const form = {};
    fields.forEach((f) => { form[f.name] = item[f.name] ?? emptyItem(fields)[f.name]; });
    form.published = !item.published;
    try {
      await api.put(`/admin${endpoint}/${item.id}`, form);
      load();
    } catch {
      toast.error("Could not update publish state");
    }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swap = items[idx + dir];
    if (!swap) return;
    const build = (src, order) => {
      const form = {};
      fields.forEach((f) => { form[f.name] = src[f.name] ?? emptyItem(fields)[f.name]; });
      form.order = order;
      return form;
    };
    try {
      await api.put(`/admin${endpoint}/${item.id}`, build(item, swap.order ?? idx + dir));
      await api.put(`/admin${endpoint}/${swap.id}`, build(swap, item.order ?? idx));
      load();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const setField = (name, value) => setEditing((s) => ({ ...s, form: { ...s.form, [name]: value } }));

  return (
    <div data-testid={`${testid}-admin`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-light text-ink">{title}</h1>
          <p className="mt-2 text-sm font-light text-ink/55">{subtitle}</p>
        </div>
        <button
          onClick={openCreate}
          data-testid={`${testid}-add-button`}
          className="bg-alta text-paper px-6 py-3 text-[10px] font-medium tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-ink"
        >
          + Add New
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {loading && <p className="text-ink/50 font-light text-sm">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="text-ink/50 font-light text-sm" data-testid={`${testid}-empty`}>Nothing here yet. Add the first one.</p>
        )}
        {items.map((item, idx) => (
          <div
            key={item.id}
            data-testid={`${testid}-row-${item.id}`}
            className="bg-paper border border-ink/10 p-4 flex flex-wrap items-center gap-4"
          >
            <div className="flex flex-col gap-1">
              <button onClick={() => move(item, -1)} disabled={idx === 0} data-testid={`${testid}-move-up-${item.id}`} aria-label="Move up" className="text-ink/40 hover:text-alta disabled:opacity-20 text-xs leading-none">▲</button>
              <button onClick={() => move(item, 1)} disabled={idx === items.length - 1} data-testid={`${testid}-move-down-${item.id}`} aria-label="Move down" className="text-ink/40 hover:text-alta disabled:opacity-20 text-xs leading-none">▼</button>
            </div>
            {(item.hero_image || item.image) && (
              <img src={item.hero_image || item.image} alt="" className="w-14 h-14 object-cover" loading="lazy" />
            )}
            <div className="flex-1 min-w-[140px]">
              <p className="font-serif text-xl text-ink">{item.title}</p>
              <p className="text-[10px] tracking-[0.18em] uppercase text-ink/45 mt-1">
                {item.year || item.date || item.category || ""}
              </p>
            </div>
            <button
              onClick={() => togglePublish(item)}
              data-testid={`${testid}-publish-toggle-${item.id}`}
              className={`text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${
                item.published ? "border-forest/40 text-forest" : "border-ink/20 text-ink/40"
              }`}
            >
              {item.published ? "Published" : "Hidden"}
            </button>
            <button onClick={() => openEdit(item)} data-testid={`${testid}-edit-${item.id}`} className="text-[10px] tracking-[0.2em] uppercase text-ink/60 hover:text-alta transition-colors">
              Edit
            </button>
            <button onClick={() => remove(item)} data-testid={`${testid}-delete-${item.id}`} className="text-[10px] tracking-[0.2em] uppercase text-ink/40 hover:text-alta transition-colors">
              Delete
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {editing && (
          <motion.div
            data-testid={`${testid}-editor-modal`}
            className="fixed inset-0 z-[80] bg-ink/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditing(null)}
          >
            <motion.form
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="bg-bone w-full max-w-2xl p-6 md:p-10 my-10 space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-3xl font-light text-ink">
                  {editing.id ? "Edit" : "New"} {title.replace(/s$/, "")}
                </h2>
                <button type="button" onClick={() => setEditing(null)} data-testid={`${testid}-editor-close`} aria-label="Close" className="text-2xl text-ink/50 hover:text-alta">×</button>
              </div>

              {fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-[10px] tracking-[0.22em] uppercase text-ink/50 mb-2">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea rows={5} value={editing.form[f.name]} onChange={(e) => setField(f.name, e.target.value)} className={`${inputCls} resize-y`} data-testid={`${testid}-field-${f.name}`} />
                  ) : f.type === "list" ? (
                    <>
                      <input
                        value={(editing.form[f.name] || []).join(", ")}
                        onChange={(e) => setField(f.name, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                        className={inputCls}
                        placeholder="Comma separated"
                        data-testid={`${testid}-field-${f.name}`}
                      />
                    </>
                  ) : f.type === "select" ? (
                    <select value={editing.form[f.name]} onChange={(e) => setField(f.name, e.target.value)} className={inputCls} data-testid={`${testid}-field-${f.name}`}>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-3 text-sm text-ink/70">
                      <input type="checkbox" checked={!!editing.form[f.name]} onChange={(e) => setField(f.name, e.target.checked)} data-testid={`${testid}-field-${f.name}`} className="w-4 h-4 accent-[#C82323]" />
                      Visible on the site
                    </label>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type={f.type === "number" ? "number" : "text"}
                        value={editing.form[f.name]}
                        onChange={(e) => setField(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                        className={inputCls}
                        data-testid={`${testid}-field-${f.name}`}
                      />
                      {f.type === "image" && editing.form[f.name] && (
                        <img src={editing.form[f.name]} alt="preview" className="h-20 object-cover border border-ink/10" />
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div className="flex gap-4 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  data-testid={`${testid}-save-button`}
                  className="bg-alta text-paper px-8 py-3.5 text-[10px] font-medium tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-ink disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" onClick={() => setEditing(null)} data-testid={`${testid}-cancel-button`} className="text-[10px] tracking-[0.22em] uppercase text-ink/50 hover:text-ink">
                  Cancel
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
